import { ReactNode } from 'react';

import { RecursivePartial } from '@e-origin/shared';

import { EucdmCode, List, TextInput } from '@components';

export interface Container {
  identificationNumber: string;
  seals?: Seal[];
}

export interface Seal {
  identificationNumber: string;
}

interface ContainerListProps {
  containers: RecursivePartial<Container>[];
  onChange: (containers: Container[]) => void;
  disabled?: boolean;
  isExport?: boolean;
  rightSection?: ReactNode;
  'data-pw'?: string;
}

export const ContainerList: React.FC<ContainerListProps> = ({ containers, onChange, disabled = false, isExport = false, rightSection, 'data-pw': dataPw }) => {
  return (
    <List
      items={containers as Container[]}
      onChange={onChange}
      disabled={disabled}
      addButtonLabel="Add Container"
      createNewItem={() => ({ identificationNumber: '', seals: [] })}
      data-pw={dataPw}
    >
      {(container, containerIdx, containerHelpers) => (
        <List.Element
          onRemove={containerHelpers.remove}
          canRemove={containerHelpers.canRemove}
          disabled={disabled}
          variant="paper"
          data-pw={dataPw ? `${dataPw}-${containerIdx}` : `container-${containerIdx}`}
        >
          <EucdmCode path="transportInformation.transportEquipment.containers">
            <TextInput
              label={`Container ${containerIdx + 1}`}
              value={container.identificationNumber}
              onChange={(evt) => containerHelpers.update({ ...container, identificationNumber: evt.target.value })}
              disabled={disabled}
              size="lg"
              data-pw={dataPw ? `${dataPw}-${containerIdx}-input` : `container-${containerIdx}-input`}
              rightSection={rightSection}
            />
          </EucdmCode>

          {isExport && (
            <List
              items={container.seals || []}
              onChange={(newSeals) => containerHelpers.update({ ...container, seals: newSeals })}
              disabled={disabled}
              addButtonLabel="Add Seal"
              addButtonSize="sm"
              addButtonVariant="light"
              createNewItem={() => ({ identificationNumber: '' })}
              spacing="xs"
              data-pw={dataPw ? `${dataPw}-${containerIdx}-seal-list` : `container-${containerIdx}-seal-list`}
            >
              {(seal, sealIdx, sealHelpers) => (
                <List.Element
                  onRemove={sealHelpers.remove}
                  canRemove={sealHelpers.canRemove}
                  disabled={disabled}
                  data-pw={dataPw ? `${dataPw}-${containerIdx}-seal-${sealIdx}` : `container-${containerIdx}-seal-${sealIdx}`}
                  ml="sm"
                  align="center"
                >
                  <EucdmCode path="transportInformation.transportEquipment.seals.identificationNumber">
                    <TextInput
                      label={`Seal ${sealIdx + 1}`}
                      value={seal.identificationNumber}
                      onChange={(evt) => sealHelpers.update({ ...seal, identificationNumber: evt.target.value })}
                      disabled={disabled}
                      size="lg"
                      data-pw={dataPw ? `${dataPw}-${containerIdx}-seal-${sealIdx}-input` : `container-${containerIdx}-seal-${sealIdx}-input`}
                    />
                  </EucdmCode>
                </List.Element>
              )}
            </List>
          )}
        </List.Element>
      )}
    </List>
  );
};
